import React, { useCallback, useEffect, useState } from "react";
import CabPage from "../CabPage";
import "../css/analytics.css";
import { Icon, P } from "../loads/common";
import { fetchAnalytics } from "../../utils/matchingApi";
import { CARDS, FUNNEL, LINES, SCOPE_TITLE } from "../analytics/config";
import { fmtDay, nf } from "../analytics/format";
import Funnel from "../analytics/Funnel";
import SeriesChart from "../analytics/SeriesChart";

/**
 * «Аналитика» — no wireframe; built in the visual language of «Мои грузы» / «Автопарк».
 * Data: GET /m/analytics?days=N (matching service), scope depends on the user's role:
 * ADMIN → platform, DISPATCHER/CLIENT → shipper, COMPANY/DRIVER → carrier.
 * KPI formulas follow ТЗ §11; null KPIs are shown as «—».
 */
const PERIODS = [7, 30, 90];

const Card = ({ card, data }) => {
  const v = data?.[card.src]?.[card.key];
  const empty = v == null;
  return (
    <article className="metric" title={card.tip}>
      <span className="metric-icon">
        <Icon d={P[card.icon]} />
      </span>
      <div>
        <b className={empty ? "null" : ""}>{empty ? "—" : card.fmt(v)}</b>
        <p>
          {card.label}
          <i className="an-info" aria-label={card.tip}>?</i>
        </p>
        {empty && card.src === "kpis" && <small>Недостаточно данных</small>}
      </div>
    </article>
  );
};

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchAnalytics(days)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days]);

  useEffect(() => load(), [load]);

  const scope = data?.scope && CARDS[data.scope] ? data.scope : "platform";
  const [title, subtitle] = data ? SCOPE_TITLE[scope] : ["Аналитика", "Показатели подбора, откликов и сделок за выбранный период."];
  const totals = data?.totals || {};
  const hasActivity =
    data &&
    (["loads_published", "matches", "pushes_sent", "viewed", "responses", "counter_offers", "deals"].some((k) => totals[k] > 0) ||
      (data.series || []).some((p) => p.loads || p.matches || p.responses || p.deals));
  const routes = data?.top_routes || [];

  let body;
  if (error && !data) {
    body = (
      <div className="an-state" role="alert">
        <b>Не удалось загрузить аналитику</b>
        {error.status === 401 || error.status === 403 ? "Нет доступа. Войдите заново." : error.status === 404 ? "Сервис аналитики пока недоступен." : error.message || "Ошибка сети"}
        <div>
          <button className="button primary" type="button" onClick={load}>Повторить</button>
        </div>
      </div>
    );
  } else if (!data) {
    body = (
      <>
        <section className="an-kpis" aria-busy="true">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="an-skel" style={{ height: 118 }} />
          ))}
        </section>
        <div className="an-skel" style={{ height: 280, marginTop: 18 }} />
      </>
    );
  } else {
    body = (
      <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
        <section className="an-kpis" aria-label="Показатели">
          {CARDS[scope].map((c) => (
            <Card key={`${c.src}.${c.key}`} card={c} data={data} />
          ))}
        </section>

        {!hasActivity ? (
          <div className="an-state">
            <b>Недостаточно данных за период</b>
            Попробуйте выбрать больший период или вернитесь, когда появятся грузы и отклики.
          </div>
        ) : (
          <>
            <div className="an-grid">
              <section className="an-panel">
                <h2>Воронка</h2>
                <p>От подбора до сделки за выбранный период</p>
                <Funnel steps={FUNNEL[scope]} totals={totals} />
              </section>
              <section className="an-panel">
                <h2>Динамика по дням</h2>
                <p>Нажмите на элемент легенды, чтобы скрыть линию</p>
                <SeriesChart series={data.series || []} lines={LINES[scope]} />
              </section>
            </div>

            <section className="results an-routes">
              <div className="results-heading">
                <div>
                  <h2>Топ маршрутов</h2>
                  <p>Самые частые направления за период</p>
                </div>
              </div>
              {routes.length ? (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Маршрут</th>
                        <th>Грузов</th>
                        <th>Откликов</th>
                        <th>Сделок</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((r, i) => (
                        <tr key={`${r.from}-${r.to}-${i}`}>
                          <td>
                            <strong>{r.from || "—"}</strong> <i className="route-arrow">→</i> <strong>{r.to || "—"}</strong>
                          </td>
                          <td>{nf(r.loads)}</td>
                          <td>{nf(r.responses)}</td>
                          <td>{nf(r.deals)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="an-state" style={{ marginTop: 0 }}>Недостаточно данных за период</div>
              )}
            </section>
          </>
        )}
        {error && (
          <div className="an-state" role="alert" style={{ marginTop: 14, padding: 16 }}>
            Не удалось обновить данные: {error.message}{" "}
            <button className="button" type="button" onClick={load}>Повторить</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <CabPage name="analytics">
      <main id="main">
        <header className="page-heading">
          <div>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </header>

        <div className="an-controls">
          <div aria-label="Период" className="load-tabs" role="tablist">
            {PERIODS.map((d) => (
              <button key={d} aria-selected={days === d} className={days === d ? "active" : ""} role="tab" type="button" onClick={() => setDays(d)}>
                {d} дней
              </button>
            ))}
          </div>
          {data?.range && (
            <span className="an-range">
              {fmtDay(data.range.from)} — {fmtDay(data.range.to)}
            </span>
          )}
        </div>

        {body}
      </main>
    </CabPage>
  );
}
